<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="row align-items-center row-gap-3 mb-4">
        <div class="col-sm-6">
          <div class="d-flex align-items-center flex-wrap row-gap-3">
            <h6 class="fw-medium d-inline-flex align-items-center me-2">
              <router-link :to="{ name: 'crm.deals.list' }">
                <i class="ti ti-arrow-left me-2"></i>Deals
              </router-link>
              <span class="text-gray d-inline-flex ms-2">/ {{ deal?.name || "—" }}</span>
            </h6>
            <div v-if="deal?.pipeline?.name" class="dropdown">
              <a href="javascript:void(0);" class="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                <i class="ti ti-git-branch me-1"></i>{{ deal.pipeline.name }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li class="px-2 small text-muted">Current pipeline</li>
                <li><hr class="dropdown-divider" /></li>
                <li><a class="dropdown-item rounded-1 disabled">{{ deal.pipeline.name }}</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-sm-6">
          <div class="d-flex justify-content-sm-end">
            <div class="head-icons ms-2">
              <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
                <i class="ti ti-chevrons-up"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading && !deal" class="card border-0">
        <div class="card-body text-muted">Loading deal...</div>
      </div>
      <div v-else-if="!deal" class="card border-0">
        <div class="card-body text-danger">Deal not found.</div>
      </div>

      <div v-else class="row">
        <div class="col-xl-4 theiaStickySidebar">
          <div class="card card-bg-1">
            <div class="card-body p-0">
              <span class="avatar avatar-xl border text-dark bg-white rounded-circle m-auto d-flex mb-2">
                {{ initials(deal.name) }}
              </span>

              <div class="text-center px-3 pb-3 border-bottom">
                <h5 class="d-flex align-items-center justify-content-center mb-1">
                  {{ deal.name }}
                  <span class="avatar avatar-sm avatar-rounded bg-light ms-2">
                    <i class="ti ti-star-filled text-warning fs-14"></i>
                  </span>
                </h5>
                <p class="text-dark mb-1">{{ deal.client?.name || deal.company_name || "—" }}</p>
                <div class="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                  <span class="badge badge-dark-transparent">
                    <i class="ti ti-lock me-1"></i>{{ titleCase(deal.visibility || "public") }}
                  </span>
                  <span class="badge" :class="statusClass(deal.status)">
                    {{ deal.status || "Open" }}
                  </span>
                </div>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Deal Information</h6>
                <kv-row label="Date Created">{{ formatDate(deal.created_at) }}</kv-row>
                <kv-row label="Probability">{{ deal.probability != null ? `${deal.probability}%` : "—" }}</kv-row>
                <kv-row label="Value">{{ money(deal.amount, deal.currency) }}</kv-row>
                <kv-row label="Due Date">{{ formatDate(deal.expected_close_date, "date") }}</kv-row>
                <kv-row label="Follow Up">{{ formatDate(deal.next_follow_up, "date") }}</kv-row>
                <kv-row label="Source">{{ deal.source || "—" }}</kv-row>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Owner</h6>
                <div v-if="deal.owner" class="d-flex align-items-center">
                  <span class="avatar avatar-md avatar-rounded me-2">
                    <span class="d-inline-flex w-100 h-100 align-items-center justify-content-center bg-light">
                      {{ initials(ownerName) }}
                    </span>
                  </span>
                  <div>
                    <div class="fw-medium">{{ ownerName }}</div>
                    <small class="text-muted">{{ deal.owner.email || "No email" }}</small>
                  </div>
                </div>
                <div v-else class="text-muted">Unassigned</div>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Tags</h6>
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <span v-for="tag in deal.tags || []" :key="tag" class="badge" :class="tagClass(tag)">{{ tag }}</span>
                  <span v-if="!deal.tags?.length" class="text-muted">—</span>
                </div>
              </div>

              <div class="p-3">
                <h6 class="mb-2">Other Information</h6>
                <kv-row label="Stage">{{ deal.stage?.name || "—" }}</kv-row>
                <kv-row label="Priority">{{ deal.priority || "—" }}</kv-row>
                <kv-row label="Last Modified">{{ formatDate(deal.updated_at) }}</kv-row>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="bg-white rounded p-3 mb-3">
            <h4 class="fw-semibold mb-3">Deal Pipeline Status</h4>
            <div class="pipeline-list">
              <ul>
                <li>
                  <a href="javascript:void(0);" class="bg-purple">{{ deal.stage?.name || "Stage" }}</a>
                </li>
              </ul>
            </div>
          </div>

          <crm-engagement-panel
            entity-type="deal"
            :entity-id="deal.id"
            :default-email="deal.contact_email || deal.client?.email || ''"
            note-placeholder="Add an internal note about this deal"
          />
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a></p>
    </div>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import CrmEngagementPanel from "@/components/crm/CrmEngagementPanel.vue";
import { useDealStore } from "@/stores/dealStore";

const route = useRoute();
const router = useRouter();
const store = useDealStore();

const id = computed(() => {
  const value = Number(route.params.id);
  return Number.isFinite(value) ? value : null;
});

const deal = computed(() => (id.value != null ? store.byId?.[id.value] : null));
const loading = computed(() => store.loading);
const ownerName = computed(() => {
  const owner = deal.value?.owner;
  if (!owner) return "";
  return [owner.first_name, owner.last_name].filter(Boolean).join(" ") || owner.email || "";
});

async function loadDeal(targetId) {
  if (!targetId) {
    router.replace({ name: "crm.deals.list" });
    return;
  }

  if (!store.byId?.[targetId]) {
    try {
      await store.fetchOne(targetId);
    } catch {}
  }
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function initials(value = "") {
  return String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "DL";
}

function money(amount, currency = "KES") {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString()}`;
  }
}

function formatDate(value, mode = "datetime") {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return mode === "date" ? date.toISOString().slice(0, 10) : date.toLocaleString();
}

function titleCase(value = "") {
  return String(value || "public").replace(/\w\S*/g, (word) => word[0].toUpperCase() + word.slice(1).toLowerCase());
}

function statusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "won":
      return "badge-success-transparent";
    case "lost":
      return "badge-danger-transparent";
    default:
      return "badge-warning-transparent";
  }
}

function tagClass(tag = "") {
  const normalized = tag.toLowerCase();
  if (normalized.includes("rated")) return "badge-soft-warning";
  if (normalized.includes("collab")) return "badge-soft-success";
  return "badge-dark-transparent";
}

const KvRow = defineComponent({
  name: "KvRow",
  props: { label: { type: String, default: "" } },
  setup(props, { slots }) {
    return () =>
      h("div", { class: "d-flex align-items-center justify-content-between mb-2" }, [
        h("span", { class: "d-inline-flex align-items-center" }, [h("i", { class: "ti ti-dots me-2" }), props.label]),
        h("p", { class: "text-dark mb-0 text-end" }, slots.default ? slots.default() : "—"),
      ]);
  },
});

onMounted(async () => {
  await loadDeal(id.value);
});

watch(id, async (value, previous) => {
  if (value && value !== previous) {
    await loadDeal(value);
  }
});
</script>

<style scoped>
@media (max-width: 767.98px) {
  .row.align-items-center.row-gap-3.mb-4 > [class*="col-"] {
    width: 100%;
  }

  .row.align-items-center.row-gap-3.mb-4 .d-flex.justify-content-sm-end {
    justify-content: flex-start !important;
  }

  .card-body.p-0 {
    overflow: hidden;
  }
}
</style>


